import { useEffect, useState } from "react";
import api from "../api/axios";
import { useParams, Link } from "react-router-dom";
import { FaArrowLeft, FaSave, FaBoxOpen, FaUser, FaMoneyBillWave, FaCalendarAlt, FaCheckCircle } from "react-icons/fa";
import AlertBox from "../components/AlertBox";
import { useTranslation } from "react-i18next";

export default function AdminOrderDetails() {
    const { id } = useParams();
    const { t } = useTranslation();

    const [order, setOrder] = useState(null);
    const [status, setStatus] = useState("");
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);

    const fetchOrder = async () => {
        setErr("");
        try {
            const res = await api.get(`/admin/orders/${id}`);
            const payload = res.data.data ?? res.data;
            const o = payload.data ?? payload;

            setOrder(o);
            setStatus(o.status || "");
        } catch (e) {
            setErr(e.response?.data?.message || t("admin_order_details_load_failed"));
        }
    };

    useEffect(() => {
        fetchOrder();
    }, [id]);

    const getStatusInfo = (s) => {
        switch (s?.toLowerCase()) {
            case "paid": return { color: "success", label: t("status_paid") };
            case "pending": return { color: "warning", label: t("status_pending") };
            case "shipped": return { color: "primary", label: t("status_shipped") };
            case "cancelled": return { color: "danger", label: t("status_cancelled") };
            default: return { color: "secondary", label: s || t("status_unknown") };
        }
    };

    const saveStatus = async () => {
        setBusy(true);
        setErr("");
        setMsg("");

        try {
            await api.patch(`/admin/orders/${id}/status`, { status });
            setMsg(t("admin_order_details_update_success"));
            fetchOrder();
        } catch (e) {
            setErr(e.response?.data?.message || t("admin_order_details_status_failed"));
        } finally {
            setBusy(false);
        }
    };

    if (err) {
        return (
            <div className="container-fluid p-4">
                <AlertBox type="danger" message={err} onClose={() => setErr("")} />
                <Link to="/admin/orders" className="btn btn-outline-secondary mt-3 shadow-sm">
                    <FaArrowLeft className="me-2" /> {t("admin_order_details_back_full")}
                </Link>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="container-fluid py-5 text-center">
                <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} />
                <p className="mt-3 text-muted fw-bold">{t("admin_order_details_loading")}</p>
            </div>
        );
    }

    const currentStatus = getStatusInfo(order.status);

    return (
        <div className="container-fluid p-0 pb-5">
            <div className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3">
                <div className="d-flex align-items-center">
                    <FaBoxOpen className="fs-3 text-primary me-3" />
                    <h2 className="m-0 fw-bold text-dark">{t("admin_order_details_title")}{order.id}</h2>
                    <span className={`badge bg-${currentStatus.color} ms-3 px-3 py-2 rounded-pill shadow-sm fs-6`}>
                        {currentStatus.label}
                    </span>
                </div>
                <Link to="/admin/orders" className="btn btn-outline-secondary shadow-sm fw-bold">
                    <FaArrowLeft className="me-2" /> {t("admin_order_details_back")}
                </Link>
            </div>

            <AlertBox type="success" message={msg} onClose={() => setMsg("")} />

            <div className="row g-4">
                <div className="col-lg-8">
                    <div className="card shadow-sm border-0 rounded-4 h-100">
                        <div className="card-header bg-white pt-4 pb-0 border-bottom-0">
                            <h5 className="fw-bold m-0"><FaBoxOpen className="text-muted me-2" /> {t("admin_order_details_items")}</h5>
                        </div>
                        <div className="card-body mt-2">
                            <div className="table-responsive">
                                <table className="table align-middle text-center mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="py-3 text-start ps-3">{t("admin_order_details_col_product")}</th>
                                            <th className="py-3">{t("admin_order_details_col_qty")}</th>
                                            <th className="py-3">{t("admin_order_details_col_price")}</th>
                                            <th className="py-3">{t("admin_order_details_col_line_total")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {order.items?.map((it) => (
                                            <tr key={it.id}>
                                                <td className="fw-bold text-dark text-start ps-3">
                                                    {it.product?.name || t("admin_order_details_unavailable")}
                                                </td>
                                                <td>
                                                    <span className="badge bg-light text-dark border px-3 py-2 fs-6">
                                                        {it.qty}
                                                    </span>
                                                </td>
                                                <td className="text-muted fw-bold">${it.price_snapshot}</td>
                                                <td className="fw-bold text-success fs-5">${it.line_total}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {(!order.items || order.items.length === 0) && (
                                <div className="alert alert-warning text-center mt-3 mb-0">
                                    {t("admin_order_details_no_items")}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-lg-4">
                    <div className="card shadow-sm border-0 rounded-4 mb-4 bg-primary text-white" style={{ background: "linear-gradient(135deg, #0d6efd 0%, #0dcaf0 100%)" }}>
                        <div className="card-body p-4">
                            <h5 className="fw-bold mb-4 border-bottom pb-2 border-light border-opacity-25">{t("admin_order_details_summary")}</h5>

                            <div className="d-flex align-items-center mb-3">
                                <FaUser className="fs-4 me-3 opacity-75" />
                                <div>
                                    <small className="d-block opacity-75">{t("admin_order_details_customer")}</small>
                                    <span className="fw-bold fs-5">{order.user?.name || order.user_id}</span>
                                </div>
                            </div>

                            <div className="d-flex align-items-center mb-3">
                                <FaCalendarAlt className="fs-4 me-3 opacity-75" />
                                <div>
                                    <small className="d-block opacity-75">{t("admin_order_details_date")}</small>
                                    <span className="fw-bold fs-5">{order.created_at ? order.created_at.slice(0, 10) : "-"}</span>
                                </div>
                            </div>

                            <div className="d-flex align-items-center mt-4 pt-3 border-top border-light border-opacity-25">
                                <FaMoneyBillWave className="fs-2 me-3 opacity-75" />
                                <div>
                                    <small className="d-block opacity-75">{t("admin_order_details_total")}</small>
                                    <h2 className="m-0 fw-bold">${order.total}</h2>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card shadow-sm border-0 rounded-4">
                        <div className="card-body p-4">
                            <h5 className="fw-bold mb-3"><FaCheckCircle className="text-muted me-2" /> {t("admin_order_details_update_status")}</h5>

                            <label className="form-label text-muted small fw-bold">{t("admin_order_details_select_status")}</label>
                            <select
                                className="form-select form-select-lg mb-4 shadow-none border-2 fw-bold"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                style={{ borderColor: "#e9ecef", cursor: "pointer" }}
                            >
                                <option value="pending">⏳ {t("status_pending")}</option>
                                <option value="paid">✅ {t("status_paid")}</option>
                                <option value="shipped">🚚 {t("status_shipped")}</option>
                                <option value="cancelled">❌ {t("status_cancelled")}</option>
                            </select>

                            <button
                                className="btn btn-dark w-100 py-2 fw-bold d-flex align-items-center justify-content-center shadow-sm"
                                onClick={saveStatus}
                                disabled={busy || status === order.status}
                            >
                                {busy ? (
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                ) : (
                                    <FaSave className="me-2" />
                                )}
                                {busy ? t("admin_order_details_updating") : t("admin_order_details_save_changes")}
                            </button>

                            <small className="text-muted d-block mt-3 text-center">
                                {t("admin_order_details_status_hint")}
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}